import { REDUX_ACTIONS, Configs } from 'utils/constants';
import { exists, isArray } from 'utils/common';
import {
  Actions,
  PrayerRequests,
  Devotionals,
  Broadcasts,
  Stories,
  Users,
  ReferenceLists,
  OrgUsers,
  SearchQuery,
  Organizations,
  OnboardingFlows,
  ConversationPrompts,
  SuggestedTopics,
  FilterMethod,
} from 'utils/gloo-types';
import { timeNow } from 'utils/index';

export type DataKeys =
  | 'prayerRequests'
  | 'conversationPrompts'
  | 'actions'
  | 'devotionals'
  | 'broadcasts'
  | 'stories'
  | 'users'
  | 'inboxUsers'
  | 'groups'
  | 'onboardingFlows'
  | 'orgUsers'
  | 'organizations'
  | 'tags'
  | 'topics'
  | 'broadcastLog'
  | 'suggestedTopics';

export interface DataState {
  prayerRequests: {
    filters: SearchQuery;
    data: PrayerRequests[];
    count: number;
  };
  stories: {
    filters: SearchQuery;
    data: Stories[];
    count: number;
  };
  users: {
    filters: SearchQuery;
    data: Users[];
    count: number;
  };
  inboxUsers: {
    filters: SearchQuery;
    data: Users[];
    count: number;
  };
  devotionals: {
    filters: SearchQuery;
    data: Devotionals[];
    count: number;
  };
  organizations: {
    filters: SearchQuery;
    data: Organizations[];
    count: number;
  };
  conversationPrompts: {
    filters: SearchQuery;
    data: ConversationPrompts[];
    count: number;
  };
  actions: {
    data: Actions[];
  };
  onboardingFlows: {
    data: OnboardingFlows[];
  };
  broadcasts: {
    data: Broadcasts[];
  };
  broadcastLog: {
    filters: SearchQuery;
    data: Broadcasts[];
    count: number;
  };
  groups: {
    data: ReferenceLists[];
  };
  tags: {
    data: ReferenceLists[];
  };
  orgUsers: {
    data: OrgUsers[];
  };
  topics: {
    data: ReferenceLists[];
  };
  suggestedTopics: {
    data: SuggestedTopics[];
  };
  lastCachedTime: {
    prayerRequests: number | null;
    conversationPrompts: number | null;
    actions: number | null;
    devotionals: number | null;
    stories: number | null;
    broadcasts: number | null;
    users: number | null;
    inboxUsers: number | null;
    groups: number | null;
    orgUsers: number | null;
    onboardingFlows: number | null;
    organizations: number | null;
    tags: number | null;
    broadcastLog: number | null;
    topics: number | null;
    suggestedTopics: number | null;
  };
}

export const initialState: DataState = {
  prayerRequests: {
    filters: {
      entity: 'prayerRequests',
      searchString: '',
      searchFields: ['text', 'user.firstName', 'user.lastName'],
      orderBy: 'createdAt',
      orderDirection: 'DESC',
      filters: [],
      permissionGroupScopingKey: 'prayer',
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  actions: {
    data: [],
  },
  devotionals: {
    filters: {
      entity: 'devotionals',
      searchString: '',
      searchFields: ['name', 'description'],
      orderBy: 'createdAt',
      orderDirection: 'DESC',
      filters: [],
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  organizations: {
    filters: {
      entity: 'organizations',
      searchString: '',
      searchFields: ['name', 'id'],
      jsonSearchFields: ['config.smsNumber', 'config.info.contactName', 'config.info.contactEmail'],
      orderBy: 'createdAt',
      orderDirection: 'ASC',
      filters: [],
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  conversationPrompts: {
    filters: {
      entity: 'conversationPrompts',
      filters: [
        {
          field: 'status',
          method: FilterMethod.equalTo,
          compareValue1: 'Published',
        },
      ],
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  onboardingFlows: {
    data: [],
  },
  stories: {
    filters: {
      entity: 'stories',
      searchString: '',
      searchFields: ['title', 'content', 'user.firstName', 'user.lastName'],
      orderBy: 'createdAt',
      orderDirection: 'DESC',
      filters: [],
      permissionGroupScopingKey: 'stories',
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  users: {
    filters: {
      searchString: '',
      searchFields: ['firstName', 'lastName'],
      entity: 'users',
      orderBy: 'lastName',
      orderDirection: 'ASC',
      filters: [],
      permissionGroupScopingKey: 'people',
      pagination: { page: 1, limit: Configs.Pagination.DefaultLimit },
    },
    data: [],
    count: 0,
  },
  inboxUsers: {
    filters: {
      searchString: '',
      searchFields: ['firstName', 'lastName'],
      entity: 'users',
      orderBy: 'lastName',
      orderDirection: 'ASC',
      filters: [],
      pagination: { page: 1, limit: 20 },
    },
    data: [],
    count: 0,
  },
  broadcasts: {
    data: [],
  },
  broadcastLog: {
    filters: {
      searchString: '',
      searchFields: ['message'],
      entity: 'broadcasts',
      orderBy: 'createdAt',
      orderDirection: 'DESC',
      filters: [],
      pagination: { page: 1, limit: 15 },
    },
    data: [],
    count: 0,
  },
  groups: {
    data: [],
  },
  tags: {
    data: [],
  },
  orgUsers: {
    data: [],
  },
  topics: {
    data: [],
  },
  suggestedTopics: {
    data: [],
  },
  lastCachedTime: {
    prayerRequests: null,
    conversationPrompts: null,
    actions: null,
    devotionals: null,
    stories: null,
    broadcasts: null,
    users: null,
    inboxUsers: null,
    groups: null,
    tags: null,
    onboardingFlows: null,
    orgUsers: null,
    organizations: null,
    broadcastLog: null,
    topics: null,
    suggestedTopics: null,
  },
};

export default function dataReducer(state = initialState, action: any): DataState {
  // Bad way to get around typescript errors.
  const stateAny: any = state;

  switch (action.type) {
    case REDUX_ACTIONS.CHANGE_ORG:
      return { ...initialState, organizations: stateAny.organizations };

    case REDUX_ACTIONS.SET_DATA:
      if (!exists(stateAny[action.key]) || (!action.data && !isArray(action.data))) {
        return state;
      }
      return {
        ...state,
        [action.key]: { ...stateAny[action.key], data: action.data },
        lastCachedTime: { ...state.lastCachedTime, [action.key]: timeNow() },
      };

    case REDUX_ACTIONS.SET_NEXT_PAGE_DATA:
      if (!exists(stateAny[action.key]) || !action.data) {
        return state;
      }
      return {
        ...state,
        [action.key]: {
          ...stateAny[action.key],
          count: action.data.count || action.data.data.length,
          data: action.data.data,
          filters: action.data.filters,
        },
        lastCachedTime: { ...state.lastCachedTime, [action.key]: timeNow() },
      };

    case REDUX_ACTIONS.UPDATE_DATA: {
      if (!exists(stateAny[action.key]) || !action.data) {
        return state;
      }
      const newState: any = { ...state };
      const updateData = newState[action.key].data;
      newState[action.key] = {
        ...newState[action.key],
        data:
          updateData.filter((a: any) => `${a.id}` === `${action.data.id}`).length > 0
            ? updateData.map((a: any) => (`${a.id}` === `${action.data.id}` ? action.data : a))
            : [...updateData, action.data],
      };
      return newState;
    }

    case REDUX_ACTIONS.DELETE_DATA: {
      if (!exists(stateAny[action.key]) || !action.id) {
        return state;
      }
      const newDeletedState: any = { ...state };
      const dataToDelete = newDeletedState[action.key].data;
      newDeletedState[action.key] = {
        ...newDeletedState[action.key],
        data: dataToDelete.filter((a: any) => `${a.id}` !== `${action.id}`),
      };
      if (exists(newDeletedState[action.key].count) && newDeletedState[action.key].count > 0) {
        newDeletedState[action.key].count -= 1;
      }
      return newDeletedState;
    }

    case REDUX_ACTIONS.UPDATE_FILTERS: {
      if (!exists(stateAny[action.key]) || !action.filters) {
        return state;
      }
      const newFilterState: any = { ...state };
      const updatedFilters = newFilterState[action.key].filters;
      newFilterState[action.key] = {
        ...newFilterState[action.key],
        filters: { ...updatedFilters, ...action.filters },
      };
      return newFilterState;
    }
    case REDUX_ACTIONS.CLEAR_CACHE_TIME:
      if (!exists(stateAny.lastCachedTime[action.key])) {
        return state;
      }
      return { ...state, lastCachedTime: { ...state.lastCachedTime, [action.key]: null } };

    case REDUX_ACTIONS.CLEAR_ALL_CACHE_TIME:
      return { ...state, lastCachedTime: initialState.lastCachedTime };

    case REDUX_ACTIONS.LOGOUT:
      return initialState;
    default:
      return state;
  }
}
